/** * タスクコメント編集コンポーネント */
<template>
  <div class="task-remarks-editor">
    <ifield-b-modal
      id="task-remarks-edit-dialog"
      ref="task-remarks-edit-dialog"
      v-bind:title="$t('theTaskRemarksEditor.editRemarks')"
      centered
      scrollable
      no-body-border
      size="lg"
      ok-variant="success"
      ignore-enforce-focus-selector=".popover"
    >
      <loading-mask isFullScreen v-bind:isLoading="isSaving" />
      <!-- フォームグループ(本体) -->
      <form-group id="task-remarks-form" ref="form" />

      <!-- modal footer -->
      <template v-slot:modal-footer="{ cancel }">
        <!-- キャンセルボタン -->
        <b-button
          size="sm"
          variant="secondary"
          v-t="'theTaskRemarksEditor.action.cancel'"
          v-on:click="cancel"
        />
        <!-- 保存ボタン -->
        <b-button
          size="sm"
          variant="success"
          v-t="'theTaskRemarksEditor.action.save'"
          v-on:click="$_saveRemarks(false)"
        />
        <!-- タスク完了ボタン -->
        <b-button
          v-if="!isTaskCompleted"
          size="sm"
          variant="success"
          v-t="'theTaskRemarksEditor.action.complete'"
          v-on:click="$_saveRemarks(true)"
        />
      </template>
    </ifield-b-modal>
    <loading-mask isFullScreen v-bind:isLoading="isLoading" />
  </div>
</template>

<script>
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import { RestApiClient } from 'ifield-core-components'
import { FUNCTION_TYPE } from '@/constants.js'
import { toServerTz } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

// タスクの区分表示用多言語化キー
const TASK_TYPE_I18N_KEY = new Map([
  [FUNCTION_TYPE.CHECKIN, 'place'],
  [FUNCTION_TYPE.REPORT, 'reportProject'],
])

export default {
  name: 'TheTaskRemarksEditor',
  setup(props, context) {
    const { handleApiError, showApiError } = useHandleApiError(props, context)
    return { handleApiError, showApiError }
  },
  data: function () {
    return {
      // スケジュールデータ
      schedule: null,
      // 対象機能タイプ
      functionType: null,
      // ロード中の場合はtrue
      isLoading: false,
      // 保存中の場合はtrue
      isSaving: false,
    }
  },
  computed: {
    /**
     * 対象のタスク
     * @type {Object}
     */
    task: function () {
      if (!this.schedule || !this.functionType) return null
      if (this.functionType == FUNCTION_TYPE.CHECKIN) {
        return this.schedule.checkinTask
      } else if (this.functionType == FUNCTION_TYPE.REPORT) {
        return this.schedule.reportTask
      }
      return null
    },
    /**
     * 対象の備考の値
     * @type {String}
     */
    remarksValue: function () {
      return this.task ? this.task.remarks : null
    },
    /**
     * タスクが完了している場合はtrue
     * @type {Boolean}
     */
    isTaskCompleted: function () {
      return this.task ? this.task.complete_flg : null
    },
    /**
     * フォーム用データ
     * @type {Array.<Object>}
     */
    forms: function () {
      return [
        {
          fieldName: 'parent_name',
          label: this.$t(
            `theTaskRemarksEditor.field.${TASK_TYPE_I18N_KEY.get(
              this.functionType
            )}`
          ),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: this.task.parent_name,
          disabled: true,
        },
        {
          fieldName: 'remarks',
          label: this.$t('theTaskRemarksEditor.field.remarks'),
          attributeType: ATTRIBUTE_TYPE.TEXT,
          value: this.remarksValue,
        },
      ]
    },
  },
  mounted: function () {
    this.$log.info('TheTaskRemarksEditor mounted.')
  },
  methods: {
    /**
     * ダイアログを表示する
     *
     * @param {Schedule} schedule スケジュールデータ(開始日時・終了日時・属性値がローカルタイムゾーンのもの)
     * @param {Number} functionType 対象機能タイプ
     */
    showModal: async function (schedule, functionType) {
      this.$_showLoading()

      this.schedule = schedule
      this.functionType = functionType

      this.$bvModal.show('task-remarks-edit-dialog')
      this.$nextTick(() => {
        this.$refs['form'].setData(this.forms)
      })
      this.$_hideLoading()
    },

    /**
     * 備考を保存する
     *
     * @param {Boolean} completeTask タスクを完了する場合はtrue
     */
    $_saveRemarks: async function (completeTask) {
      this.$_showSaveLoading()

      // 値取得
      let values
      try {
        values = this.$refs['form'].getValues()
      } catch {
        this.$log.info('validation error')
        this.$_hideSaveLoading()
        return
      }

      const newRemarks = values.find((item) => item.name === 'remarks').value
      // 備考が空の時はタスクを完了できない
      if (completeTask && !newRemarks) {
        this.$log.info('Failed to complete the task due to remarks empty')
        this.$_showCompleteTaskNotAllowedAlert()
        this.$_hideSaveLoading()
        return
      }

      // PUT実行
      const requestBody = this.$_makeRequestBody(newRemarks, completeTask)
      const url = RestApiClient.createApiUrl(`schedule/${this.schedule.id}`)
      try {
        await RestApiClient.executePutApi(url, requestBody)
      } catch (error) {
        this.handleApiError(error, this.$_onSaveFailure)
        this.$_hideSaveLoading()
        return
      }

      // 終了処理
      this.$_emitSavedEvent(completeTask)
      this.$_showSavedToast(completeTask)
      this.$bvModal.hide('task-remarks-edit-dialog')
      this.$_hideSaveLoading()
    },

    /**
     * 保存失敗時処理
     * @param {Error|Response|String} error エラー
     */
    $_onSaveFailure: async function (error) {
      if (error instanceof Response && error.status === 404) {
        /**
         * スケジュールが見つから無かった時のイベント
         *
         * @param {Number} scheduleId スケジュールID
         */
        this.$emit('task-remarks-schedule-not-found', this.schedule.id)
        this.$_showScheduleNotFoundAlert()
        this.$bvModal.hide('task-remarks-edit-dialog')
      } else {
        this.showApiError(error)
      }
    },

    /**
     * リクエストボディを作成する
     *
     * @param {String} newRemarks 新しい備考の値
     * @param {Boolean} completeTask タスクを完了する場合はtrue
     * @returns {Object} リクエストボディ
     */
    $_makeRequestBody: function (newRemarks, completeTask) {
      const body = this.schedule.toRequestJSON()
      // 受け取ったスケジュールデータのうち再送する値をサーバータイムゾーンに戻す
      if (!body.allday_flg) {
        body.start_at = toServerTz(body.start_at)
        body.end_at = toServerTz(body.end_at)
      }
      for (const attr of body.attributes) {
        const attributeType = this.schedule.attributes.find(
          (a) => a.attribute_id === attr.attribute_id
        )?.attribute_type
        if (attributeType == ATTRIBUTE_TYPE.DATETIME && attr.attribute_value) {
          attr.attribute_value = toServerTz(attr.attribute_value)
        }
      }

      // 値を埋め込み
      const targetTask = body.tasks.find(
        (task) => task.function_type === this.functionType
      )
      targetTask.remarks = newRemarks
      if (completeTask) {
        targetTask.complete_flg = true
      }

      return body
    },

    /**
     * 保存完了イベントを発行する
     *
     * @param {Boolean} isTaskCompleted タスクを完了した場合はtrue
     */
    $_emitSavedEvent: function (isTaskCompleted) {
      /**
       * タスク備考の保存が完了した時のイベント
       *
       * @param {Number} scheduleId スケジュールID
       * @param {Number} functionType 機能タイプ
       * @param {Boolean} isTaskCompleted タスクを完了した場合はtrue
       */
      this.$emit(
        'task-remarks-saved',
        this.schedule.id,
        this.functionType,
        isTaskCompleted
      )
    },

    /**
     * タスク完了できないアラートを表示する
     */
    $_showCompleteTaskNotAllowedAlert: function () {
      this.$alert.fire({
        icon: 'error',
        title: this.$t('theTaskRemarksEditor.message.error'),
        text: this.$t(
          'theTaskRemarksEditor.message.completeTaskNotAllowedWithoutRemarks'
        ),
        confirmButtonText: this.$t('theTaskRemarksEditor.action.ok'),
      })
    },

    /**
     * スケジュールが見つからないアラートを表示する
     */
    $_showScheduleNotFoundAlert: function () {
      this.$alert.fire({
        icon: 'error',
        title: this.$t('theTaskRemarksEditor.message.error'),
        text: this.$t('theTaskRemarksEditor.message.scheduleNotFound'),
        confirmButtonText: this.$t('theTaskRemarksEditor.action.ok'),
      })
    },

    /**
     * 保存完了トーストを表示する
     *
     * @param {Boolean} isTaskCompleted タスクが完了された場合はtrue
     */
    $_showSavedToast: function (isTaskCompleted) {
      const text = isTaskCompleted
        ? this.$t('theTaskRemarksEditor.message.completedTask')
        : this.$t('theTaskRemarksEditor.message.savedRemarks')
      this.$toast.fire({
        position: 'top',
        icon: 'success',
        title: text,
        confirmButtonText: this.$t('theTaskRemarksEditor.action.ok'),
        confirmButtonColor: '#6c757d',
        timer: 2000,
      })
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },

    /**
     * 保存中ローディングを表示する
     */
    $_showSaveLoading: function () {
      this.isSaving = true
    },

    /**
     * 保存中ローディングを非表示にする
     */
    $_hideSaveLoading: function () {
      this.isSaving = false
    },
  },
}
</script>
